import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Permission, Res, SalesOrderDTO, PrintPreviewDTO, SalesOrderType, BuyerCustomerDTO } from 'core/api';
import { BackActionButton, Content, Footer, Header, PosScreen, useClickWithFeedback } from 'pos/ui';
import { PrintFacade, SalesOrderFacade } from 'pos/facades';
import { SaleModuleNextAction } from '../helpers/SaleModuleNextAction';
import { BarcodeEncoder } from 'pos/services';
import { Message, Modal, useOverlayExecutor } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BarcodeReader, ChakraUtils, SvgIcon, Toaster } from 'core/ui-next';
import { Box, HStack, VStack, Text, TableScrollArea, Table, TableCellProps, Separator, Menu, Button, Portal } from '@chakra-ui/react';
import { z } from 'zod';
import { Events } from 'core/infrastructure';
import { Close } from 'core/img';
import { CheckBoxMenuItemDef } from 'core/ui-next/controls/CheckBoxMenu';
import getTableCompanions from '../main/ticket/getTableCompanions';
import * as SalesOrderStore from './SalesOrderStore';

type SalesOrderModel = SalesOrderDTO & { SerieAndNumber: string };
const toModel = (order: SalesOrderDTO) => ({ ...order, SerieAndNumber: UI.Format.asDocumentNumber(order.Serie, order.Number) });

type Props = { initialSalesOrders: SalesOrderModel[] } & Modal.Actions<SaleModuleNextAction>;

const stateSchema = z.object({
	searchText: z.string(),
});

type FormState = z.infer<typeof stateSchema>;

function ShowSalesOrdersScreen(props: Props) {

	const {initialSalesOrders, cancel, close } = props;
	const { execute } = useOverlayExecutor();

	const [allSalesOrders, setAllSalesOrders] = React.useState<SalesOrderModel[]>(initialSalesOrders);
	const [salesOrders, setSalesOrders] = React.useState<SalesOrderModel[]>(initialSalesOrders);
	const [selectedSalesOrders, setSelectedSalesOrders] = React.useState<SalesOrderModel[]>([]);
	const [preview, setPreview] = React.useState<Nullable<PrintPreviewDTO>>(null);
	const [selectedColumn, setSelectedColumn] = React.useState<Nullable<ChakraUtils.ColumnDef<SalesOrderModel>>>(null);
	const [columnSortDirection, setColumnSortDirection] = React.useState<Nullable<ChakraUtils.SortDirection>>(null);

	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input, CheckBoxMenu} = useControlsFor<FormState>();

	const columnsDef = buildColumnsDef();

	const initialFormState: FormState = {
		searchText: '',
	};

	// No se usa useForceUpdate para forzar el remount del CheckBoxMenu de columnas,
	// ya que al cambiar el estado de showAllColumns o checkedColumns no se actualizan bien las columnas del componente de chakra en la UI.
	const [checkMenuColumnsKey, setCheckMenuColumnsKey] = React.useState<number>(0);
	const remountColumnsCheckMenu = React.useCallback(() => setCheckMenuColumnsKey(k => k + 1), []);

	// Visibilidad de columnas persistida en localStorage. La leemos una única vez al montar
	// para sembrar el estado inicial; si no hay nada guardado (primera vez) usamos los valores
	// por defecto de siempre (todas las columnas visibles).
	const persistedColumnsVisibility = React.useMemo(() => ChakraUtils.getColumnsVisibility('SHOW_SALES_ORDERS_COLUMNS_VISIBILITY_KEY'), []);

	// Estado de qué columnas están visibles en la tabla.
	// - `showAllColumns` = true  → se muestran todas las columnas y los checks individuales están desmarcados.
	// - `showAllColumns` = false → se muestran solo las columnas cuyo check individual está marcado.
	const [showAllColumns, setShowAllColumns] = React.useState<boolean>(persistedColumnsVisibility?.showAllColumns ?? true);
	const [checkedColumns, setCheckedColumns] = React.useState<Record<ChakraUtils.ColumnKey<SalesOrderModel>, boolean>>(() =>
		persistedColumnsVisibility?.checkedColumns
		?? columnsDef.reduce((acc, col) => { acc[col.key] = false; return acc; }, {} as Record<ChakraUtils.ColumnKey<SalesOrderModel>, boolean>)
	);

	// Cada vez que cambia la visibilidad de columnas (por interacción con el CheckBoxMenu),
	// la guardamos en localStorage para recuperarla en la siguiente apertura de la pantalla.
	React.useEffect(() => {
		ChakraUtils.saveColumnsVisibility({ showAllColumns, checkedColumns }, 'SHOW_SALES_ORDERS_COLUMNS_VISIBILITY_KEY');
	}, [showAllColumns, checkedColumns]);

	const clearCheckedColumns = React.useCallback(() => {
		const checkedColumns = columnsDef.reduce((acc, k) => { acc[k.key] = false; return acc; }, {} as Record<ChakraUtils.ColumnKey<SalesOrderModel>, boolean>);
		setCheckedColumns(checkedColumns);
	}, [columnsDef]);

	// Conjunto de columnas actualmente visibles.
	// - En modo SerieAndNumber se muestran siempre todas las columnas por defecto, sin alterar la
	//   preferencia guardada en localStorage (se recupera tal cual al volver a BusinessDay).
	// - En modo BusinessDay: si "Todos" está marcado se muestran todas, en caso contrario solo las
	//   que tengan su check individual activo.
	const visibleColumns = React.useMemo<Set<ChakraUtils.ColumnKey<SalesOrderModel>>>(() => {

		const allColumnsKeys = columnsDef.map(c => c.key);

		const checkedColumnsKeys = allColumnsKeys.filter(k => checkedColumns[k]);
		return new Set<ChakraUtils.ColumnKey<SalesOrderModel>>(showAllColumns ? allColumnsKeys : checkedColumnsKeys);
	}, [columnsDef, showAllColumns, checkedColumns]);

	const visibleDefs = columnsDef.filter(c => visibleColumns.has(c.key));


	// Handler para el check "Todos": al marcarlo, se muestran todas las columnas y se desmarcan los demás checks.
	// Al desmarcarlo, se mantiene el conjunto actual de checks individuales.
	const onAllCheckedChange = React.useCallback((checked: boolean) => {
		if (checked) {
			setShowAllColumns(true);
			clearCheckedColumns();
			remountColumnsCheckMenu();
		} else {
			setShowAllColumns(false);
		}
	}, [clearCheckedColumns, remountColumnsCheckMenu]);

	// Handler para cada check de columna: si se marca uno cuando "Todos" estaba activo,
	// se desmarca "Todos" para pasar a modo "solo las columnas marcadas".
	const onColumnCheckedChange = React.useCallback((column: ChakraUtils.ColumnKey<SalesOrderModel>, checked: boolean) => {
		setCheckedColumns(prev => ({ ...prev, [column]: checked }));
		if (checked && showAllColumns) {
			setShowAllColumns(false);
			remountColumnsCheckMenu();
		}
	}, [showAllColumns, remountColumnsCheckMenu]);

	const filterSalesOrders = React.useCallback((orders: SalesOrderModel[], searchText: string): SalesOrderModel[] => {
		const normalizedSearchText = searchText.trim().toLowerCase();
		if (!normalizedSearchText)
			return orders;

		const columns = buildColumnsDef();
		return orders.filter(order => columns.some(col => String(col.render(order) ?? '').toLowerCase().includes(normalizedSearchText)));
	}, []);

	const reload = React.useCallback(async (searchText: string) => {
		const orders = (await SalesOrderFacade.findByText('')).map(toModel);
		const filteredSalesOrders = filterSalesOrders(orders, searchText);
		const selectedSalesOrder = filteredSalesOrders.filter(x => selectedSalesOrders.map(x => x.Id).includes(x.Id));
		const preview = selectedSalesOrders.length ? (await PrintFacade.getSalesOrderPreview(selectedSalesOrders[0].Id))[0] : null;

		setAllSalesOrders(orders);
		setSalesOrders(filteredSalesOrders);
		setSelectedSalesOrders(selectedSalesOrder);
		setPreview(preview);

	}, [selectedSalesOrders, filterSalesOrders]);

	const onSelectSalesOrder = React.useCallback(async (selectedSalesOrder: SalesOrderModel[]) => {
		const preview = selectedSalesOrder.length ? (await PrintFacade.getSalesOrderPreview(selectedSalesOrder[0].Id))[0] : null;
		setSelectedSalesOrders(selectedSalesOrder);
		setPreview(preview);
	}, []);

	const onBarcodeRead = async (barcode: string) => {

		const idResult = await BarcodeEncoder.tryDecodeId(BarcodeEncoder.EntityType.SalesOrder, barcode);
		if (!idResult.ok) {
			await Message.show(BarcodeEncoder.isQRCode(barcode)
				? Res.Msg.MSG_QR_IS_NOT_A_VALID_PENDING_SALES_ORDER_QR(barcode)
				: Res.Msg.MSG_BARCODE_IS_NOT_A_VALID_PENDING_SALES_ORDER_BARCODE(barcode));
			return;
		}

		const salesOrder = await SalesOrderFacade.getById(idResult.value);
		if (!salesOrder || salesOrder.Id === 0 || salesOrder.Type !== SalesOrderType.Standard) {
			await Message.show(Res.Msg.MSG_SALES_ORDER_NOT_FOUND());
			return;
		}

		const order = toModel(salesOrder);
		const preview = (await PrintFacade.getSalesOrderPreview(order.Id))[0];

		formRef.current?.setValue('searchText', '');
		setSelectedSalesOrders([order]);
		setPreview(preview);
	};

	React.useEffect(() => {
		const disposeReader = BarcodeReader.start(onBarcodeRead);

		Events.on('pos:sales-order:operation-failed', async ({ msg }) => {
			await Message.show(msg);
			await reload(await formRef.current?.getValue().then(value => value.ok ? value.value.searchText : '') ?? '');
		});

		Events.on('pos:sales-order:changed', async () => reload(await formRef.current?.getValue().then(value => value.ok ? value.value.searchText : '') ?? ''));

		return () => disposeReader();
	}, [reload]);

	const onSearch = React.useCallback(async () => {
		const formValue = await formRef.current!.getValue();

		if(!formValue.ok)
			return;

		await reload(formValue.value.searchText);
	}, [reload]);

	const onFilter = React.useCallback((value: string) => {
		setSalesOrders(filterSalesOrders(allSalesOrders, value));
	}, [allSalesOrders, filterSalesOrders]);

	const onClear = React.useCallback(async () => {
		formRef.current?.setValue('searchText', '');
		await reload('');
	}, [reload]);

	function columnCheckMenuItems(onAllCheckedChange: (checked: boolean) => void, onColumnCheckedChange: (column: ChakraUtils.ColumnKey<SalesOrderModel>, checked: boolean) => void, checkedColumns: Record<string, boolean>, showAllColumns: boolean): CheckBoxMenuItemDef[] {
		const checkMenuItems: CheckBoxMenuItemDef[] = [
			{ value: 'All', title: Res.Common.CHECK_BOX_MENU_ALL_OPTION_LBL(), checked: showAllColumns, onCheckedChange: onAllCheckedChange },
			{ value: 'SerieAndNumber', title: Res.Web.FIND_PENDING_SALES_ORDERS_NUMBER_CLM(), checked: checkedColumns.SerieAndNumber, onCheckedChange: (checked) => onColumnCheckedChange('SerieAndNumber', checked) },
			{ value: 'CustomerDocumentNumber', title: Res.Web.FIND_PENDING_SALES_ORDERS_CUSTOMER_DOCUMENT_NUMBER_CLM(), checked: checkedColumns.CustomerDocumentNumber, onCheckedChange: (checked) => onColumnCheckedChange('CustomerDocumentNumber', checked) },
			{ value: 'Customer', title: Res.Web.FIND_PENDING_SALES_ORDERS_CUSTOMER_NAME_CLM(), checked: checkedColumns.Customer, onCheckedChange: (checked) => onColumnCheckedChange('Customer', checked) },
			{ value: 'CreationDate', title: Res.Web.FIND_PENDING_SALES_ORDERS_CREATION_DATE_CLM(), checked: checkedColumns.CreationDate, onCheckedChange: (checked) => onColumnCheckedChange('CreationDate', checked) },
			{ value: 'DeliveryDate', title: Res.Web.FIND_PENDING_SALES_ORDERS_DELIVERY_DATE_CLM(), checked: checkedColumns.DeliveryDate, onCheckedChange: (checked) => onColumnCheckedChange('DeliveryDate', checked) },
			{ value: 'NetAmount', title: Res.Web.FIND_PENDING_SALES_ORDERS_NET_AMOUNT_CLM(), checked: checkedColumns.NetAmount, onCheckedChange: (checked) => onColumnCheckedChange('NetAmount', checked) },
			{ value: 'PendingAmount', title: Res.Web.FIND_PENDING_SALES_ORDERS_PENDING_AMOUNT_CLM(), checked: checkedColumns.PendingAmount, onCheckedChange: (checked) => onColumnCheckedChange('PendingAmount', checked) },
			{ value: 'PrepaidAmount', title: Res.Web.FIND_PENDING_SALES_ORDERS_PREPAID_AMOUNT_CLM(), checked: checkedColumns.PrepaidAmount, onCheckedChange: (checked) => onColumnCheckedChange('PrepaidAmount', checked) },
			{ value: 'Location', title: Res.Web.FIND_PENDING_SALES_ORDERS_LOCATION_CLM(), checked: checkedColumns.Location, onCheckedChange: (checked) => onColumnCheckedChange('Location', checked) },
			{ value: 'PosName', title: Res.Web.FIND_PENDING_SALES_ORDERS_POS_CLM(), checked: checkedColumns.PosName, onCheckedChange: (checked) => onColumnCheckedChange('PosName', checked) },
			{ value: 'Notes', title: Res.Web.FIND_PENDING_SALES_ORDERS_NOTES_CLM(), checked: checkedColumns.Notes, onCheckedChange: (checked) => onColumnCheckedChange('Notes', checked) },
		];

		return checkMenuItems;
	}

	function buildColumnsDef(): ChakraUtils.ColumnDef<SalesOrderModel>[] {

		const columns: ChakraUtils.ColumnDef<SalesOrderModel>[] = [
			{ key: 'SerieAndNumber', header: Res.Web.FIND_PENDING_SALES_ORDERS_NUMBER_CLM(), render: x => x.SerieAndNumber, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'CustomerDocumentNumber', header: Res.Web.FIND_PENDING_SALES_ORDERS_CUSTOMER_DOCUMENT_NUMBER_CLM(), render: x => x.CustomerDocumentNumber, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'Customer', header: Res.Web.FIND_PENDING_SALES_ORDERS_CUSTOMER_NAME_CLM(), render: x => x.Customer.FiscalName, compare: (a: BuyerCustomerDTO, b: BuyerCustomerDTO) => a.FiscalName.localeCompare(b.FiscalName) },
			{ key: 'CreationDate', header: Res.Web.FIND_PENDING_SALES_ORDERS_CREATION_DATE_CLM(), render: x => UI.Format.asDate(x.CreationDate), compare: (a: Date, b: Date) => new Date(a).getTime() - new Date(b).getTime() },
			{ key: 'DeliveryDate', header: Res.Web.FIND_PENDING_SALES_ORDERS_DELIVERY_DATE_CLM(), render: x => UI.Format.asDateAndTime(x.DeliveryDate), compare: (a: Nullable<Date>, b: Nullable<Date>) => (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0) },
			{ key: 'NetAmount', header: Res.Web.FIND_PENDING_SALES_ORDERS_NET_AMOUNT_CLM(), render: x => UI.Format.asMoney(x.NetAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'PendingAmount', header: Res.Web.FIND_PENDING_SALES_ORDERS_PENDING_AMOUNT_CLM(), render: x => UI.Format.asMoney(x.PendingAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'PrepaidAmount', header: Res.Web.FIND_PENDING_SALES_ORDERS_PREPAID_AMOUNT_CLM(), render: x => UI.Format.asMoney(x.PrepaidAmount), compare: (a: number, b: number) => a - b, textAlign: 'end' },
			{ key: 'Location', header: Res.Web.FIND_PENDING_SALES_ORDERS_LOCATION_CLM(), render: x => x.Location, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'PosName', header: Res.Web.FIND_PENDING_SALES_ORDERS_POS_CLM(), render: x => x.PosName, compare: (a: string, b: string) => a.localeCompare(b) },
			{ key: 'Notes', header: Res.Web.FIND_PENDING_SALES_ORDERS_NOTES_CLM(), render: x => x.Notes, compare: (a: string, b: string) => a.localeCompare(b) }
		];

		return columns;
	};

	const onSortBy = React.useCallback((column: ChakraUtils.ColumnDef<SalesOrderModel>) => {
		if (!column.compare)
			return;

		const currentSortDirection = selectedColumn?.key === column.key ? columnSortDirection : null;
		const nextDirection: ChakraUtils.SortDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';

		setSelectedColumn(column);
		setColumnSortDirection(nextDirection);
		setSalesOrders(prevSalesOrders => ChakraUtils.sortRows(prevSalesOrders, column, nextDirection));
	}, [selectedColumn, columnSortDirection]);

	const showNotes = React.useCallback(async () => {
		if (selectedSalesOrders.length !== 0 && selectedSalesOrders[0].Notes)
			await Message.show(selectedSalesOrders[0].Notes);
	}, [selectedSalesOrders]);

	const reopen = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		const result = await Command.execute('reopenSalesOrder', selected[0]);
		if (result.ok)
			close({ type: 'loadTicket', ticketId: result.value.reopenedTicketId });
	}, [selectedSalesOrders, close]);

	const updateTableCompanions = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		const result = await getTableCompanions({
			FriendlyName: '',
			TableCompanions: selectedSalesOrders[0].TableCompanions,
			Location: {
				SaleCenterName: '',
				SaleLocationName: ''
			}
		}, 9999);

		if (!result.ok)
			return;

		await execute({
			action: async () => {
				await SalesOrderStore.setTableCompanions(selectedSalesOrders[0].Id, result.value);
			}
		});
	}, [selectedSalesOrders, execute]);

	const sendEmail = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('sendSalesOrderEmail', selected[0]);
	}, [selectedSalesOrders]);

	const showPayments = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('showSalesOrderPayments', selected[0]);
	}, [selectedSalesOrders]);

	const updateCustomer = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('updateSalesOrderCustomer', selected[0]);
	}, [selectedSalesOrders]);

	const convertToDeliveryNote = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		if (selected.length > 1) {
			await Message.show(Res.Msg.MSG_CANNOT_CONVERT_MULTIPLE_SALES_ORDERS_TO_DELIVERY_NOTE());
			return;
		}

		const result = await SalesOrderStore.convertToDeliveryNote(selected.map(x => x.Id));

		if (!result.ok) {
			return;
		}

		var documentNumber = UI.Format.asDocumentNumber(result.value.SerialNumber.SerieName, result.value.SerialNumber.Number);
		Toaster.info(Res.Msg.MSG_SALES_ORDERS_CONVERTED_TO_DELIVERY_NOTE(documentNumber));
		await reload(await formRef.current?.getValue().then(value => value.ok ? value.value.searchText : '') ?? '');
	}, [selectedSalesOrders, reload]);

	const closeOrder = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('closeSalesOrder', selected[0]);
	}, [selectedSalesOrders]);

	const cancelOrder = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('cancelSalesOrder', selected[0], false);
	}, [selectedSalesOrders]);

	const print = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('printSalesOrder', selected[0]);
	}, [selectedSalesOrders]);

	const prepare = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('prepareSalesOrder', selected[0]);
		await reload(await formRef.current?.getValue().then(value => value.ok ? value.value.searchText : '') ?? '');
	}, [selectedSalesOrders, reload]);

	const prepareAgain = React.useCallback(async () => {
		const selected = selectedSalesOrders;
		if (selected.length === 0)
			return;

		await Command.execute('prepareSalesOrderAgain', selected[0]);
		await reload(await formRef.current?.getValue().then(value => value.ok ? value.value.searchText : '') ?? '');
	}, [selectedSalesOrders, reload]);

	const onShowNotes = useClickWithFeedback(() => showNotes());
	const onReopen = useClickWithFeedback(() => reopen());
	const onUpdateTableCompanions = useClickWithFeedback(() => updateTableCompanions());
	const onSendEmail = useClickWithFeedback(() => sendEmail());
	const onShowPayments = useClickWithFeedback(() => showPayments());
	const onUpdateCustomer = useClickWithFeedback(() => updateCustomer());
	const onConvertToDeliveryNote = useClickWithFeedback(() => convertToDeliveryNote());
	const onCloseOrder = useClickWithFeedback(() => closeOrder());
	const onCancelOrder = useClickWithFeedback(() => cancelOrder());
	const onPrint = useClickWithFeedback(() => print());
	const onPrepare = useClickWithFeedback(() => prepare());
	const onPrepareAgain = useClickWithFeedback(() => prepareAgain());

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='SHOW_SALES_ORDERS' size='full-screen' useKeyPad={false}>
		<Header title={Res.Web.FIND_PENDING_SALES_ORDER()} image={Images.ShowDeliveryNotes} onClose={cancel} />
		<Content>
			<VStack width={'full'} height={'full'} overflow={'hidden'}>
				<VStack width={'full'} height={'full'} gap={'0'} overflow={'hidden'}>
					<HStack width={'full'} paddingX={'6'} paddingY={'3'} textAlign={'center'} justifyContent={'space-between'} gap={'20'} borderBottom={'1px solid'} borderColor={'neutral.300'}>
						<Box width={'58'} height={'8'} bg={{ base: '{colors.blue.100}', _dark: '{colors.blue.800}' }} borderRadius={'md'} padding={'0.5'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.700}', _dark: 'white' }}>
								{Res.Web.FIND_PENDING_SALES_ORDERS_NET_AMOUNT_AMOUNT_LBL(UI.Format.asMoney(salesOrders.reduce((sum, x) => sum + x.NetAmount, 0)))}
							</Text>
						</Box>
						<Box width={'58'} height={'8'} bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' }} borderRadius={'md'} padding={'0.5'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.700}', _dark: 'white' }}>
								{Res.Web.FIND_PENDING_SALES_ORDERS_PENDING_AMOUNT_LBL(UI.Format.asMoney(salesOrders.reduce((sum, x) => sum + x.PendingAmount, 0)))}
							</Text>
						</Box>
						<Box width={'58'} height={'8'} bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' }} borderRadius={'md'} padding={'0.5'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.700}', _dark: 'white' }}>
								{Res.Web.FIND_PENDING_SALES_ORDERS_PREPAID_AMOUNT_LBL(UI.Format.asMoney(salesOrders.reduce((sum, x) => sum + x.PrepaidAmount, 0)))}
							</Text>
						</Box>
					</HStack>
					<HStack width={'full'} height={'full'} padding={'6'} gap={'6'} overflow={'hidden'}>
						<VStack w={'stretch'} height={'full'} gap={'9'} overflow={'hidden'}>
							<HStack width={'full'}>
								<Form ref={formRef} initialValue={initialFormState} schema={stateSchema} style={{ width: '100%'}}>
									<HStack width={'full'} gap={'9'} justifyContent={'flex-start'}>
										<Box width={'stretch'} maxW={'96'}>
											<Input
												linkTo={'searchText'}
												placeholder={Res.Web.FIND_PENDING_SALES_ORDER_SEARCH_PLACEHOLDER()}
												onWatch={(value) => onFilter(value ?? '')}
												startElement={<SvgIcon src={Images.Search} width={'4'} height={'4'} cursor={'pointer'} onClick={onSearch} />}
												endElement={<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />}
												borderRadius={'4xl'}
												borderColor={'neutral.600'}
												height={'10'}
											/>
										</Box>
										{selectedSalesOrders.length > 0 && 
											<HStack gap={'2'}>
												<VStack width={'13'} gap={'1'} {...onCancelOrder} cursor={'pointer'}>
													<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
														<SvgIcon src={Images.InvoiceCancel} width={'6'} height={'6'} />
													</Box>
													<Box paddingX={'2'}>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_CANCEL_BTN()}</Text>
													</Box>
												</VStack>
												<VStack width={'13'} gap={'1'} {...onPrint} cursor={'pointer'}>
													<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
														<SvgIcon src={Images.PrintInvoice} width={'6'} height={'6'} />
													</Box>
													<Box paddingX={'2'}>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_PRINT_BTN()}</Text>
													</Box>
												</VStack>
												<VStack width={'13'} gap={'1'} {...onCloseOrder} cursor={'pointer'}>
													<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
														<SvgIcon src={Images.CloseTicket} width={'6'} height={'6'} />
													</Box>
													<Box paddingX={'2'}>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_CLOSE_BTN()}</Text>
													</Box>
												</VStack>
											</HStack>
										}
										<HStack>
											<SvgIcon src={Images.Columns} width={'6'} height={'6'} />
											<CheckBoxMenu
												key={checkMenuColumnsKey}
												items={columnCheckMenuItems(
													onAllCheckedChange,
													onColumnCheckedChange,
													checkedColumns,
													showAllColumns
												)}
												icon={<SvgIcon src={Images.ChevronDown} width={'5'} height={'5'} />}
											/>
										</HStack>
									</HStack>
								</Form>
							</HStack>
							<VStack width={'full'} height={'full'} gap={'4'} overflow={'hidden'}>
								<VStack width={'full'} height={'stretch'}>
									<HStack width={'full'} justifyContent={'flex-start'}>
										<Box minW={'4'} minH={'4'}><SvgIcon src={Images.SortByDesc} width={'4'} height={'4'} /></Box>
										<Box width={'full'}><Text fontSize={'2xs'}>{Res.Common.SORT_TABLE_COLUMNS_PLACEHOLDER()}</Text></Box>
									</HStack>
									<HStack overflow={'auto'} minW={'full'} maxW={'stretch'} height={'full'} minH={'0'} maxH={'stretch'} alignItems={'flex-start'}>
										<TableScrollArea maxWidth={'stretch'} minWidth={'full'} maxHeight={'stretch'} rounded={'md'}>
											<Table.Root size={'sm'} striped stickyHeader interactive>
												<Table.Header>
													<Table.Row>
														{visibleDefs.map((col) => (
															<Table.ColumnHeader key={col.key} onClick={() => onSortBy(col)} cursor={'pointer'} textAlign={col.textAlign}>
																<HStack gap={'0.5'} justifyContent={'flex-start'}>
																	<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{col.header}</Text>
																	{selectedColumn?.key === col.key && <SvgIcon src={columnSortDirection === 'asc' ? Images.SortByDesc : Images.SortByAsc} width={'4'} height={'4'} />}
																</HStack>
															</Table.ColumnHeader>
														))}
													</Table.Row>
												</Table.Header>
												<Table.Body>
													{(salesOrders.length === 0 || columnsDef.length === 0) && (
														<Table.Row>
															<Table.Cell colSpan={Math.max(columnsDef.length, 1)} textAlign={'center'}>
																<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.FIND_PENDING_SALES_ORDER_SEARCH_NO_RESULTS()}</Text>
															</Table.Cell>
														</Table.Row>
													)}
													{salesOrders.length > 0 && salesOrders.map((order) => {
														const cellBackGround: TableCellProps['bg'] = order.Id === selectedSalesOrders[0]?.Id ? { base: '{colors.blue.100}', _dark: '{colors.blue.800}' } : undefined;

														return (
															<Table.Row key={order.Id} onClick={() => onSelectSalesOrder([order])} cursor={'pointer'}>
																{visibleDefs.map(col => (
																	<Table.Cell background={cellBackGround} key={col.key} textAlign={col.textAlign}>
																		<Text fontSize={'sm'}>{col.render(order)}</Text>
																	</Table.Cell>
																))}
															</Table.Row>
														);
													})}
												</Table.Body>
											</Table.Root>
										</TableScrollArea>
									</HStack>
								</VStack>
							</VStack>
						</VStack>
						<VStack minW={'95'} height={'full'} gap={'4'} overflow={'hidden'}>
							{preview === null ? (
								<VStack justifyContent={'center'} alignItems={'center'} minW={'95'} height={'full'}>
									<SvgIcon src={Images.RecentSalesOrders} width={'16'} height={'16'} />
									<Text>{Res.Web.FIND_PENDING_SALES_ORDER_SELECTION_ORDER_TO_PREVIEW_LBL()}</Text>
								</VStack>
							) : (
								<>
									<HStack gap={'0'} alignItems={'flex-start'} width={'full'} justifyContent={'space-evenly'}>
										{ selectedSalesOrders.length !== 0 && selectedSalesOrders[0].Notes &&
											<VStack width={'13'} gap={'1'} {...onShowNotes} cursor={'pointer'}>
												<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
													<SvgIcon src={Images.Notes} width={'6'} height={'6'} />
												</Box>
												<Box paddingX={'2'}>
													<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_NOTES_BTN()}</Text>
												</Box>
											</VStack>
										}
										<VStack width={'13'} gap={'1'} {...onReopen} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.Refresh} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_REOPEN_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onUpdateTableCompanions} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.TableCompanions} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_TABLE_COMPANIONS_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onSendEmail} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.Send} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_SEND_EMAIL_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onShowPayments} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.ShowPayments} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_SHOW_PAYMENTS_BTN()}</Text>
											</Box>
										</VStack>
										<VStack width={'13'} gap={'1'} {...onUpdateCustomer} cursor={'pointer'}>
											<Box padding={'2'} borderRadius={'md'} borderColor={'neutral.300'} borderWidth={'1px'}>
												<SvgIcon src={Images.User} width={'6'} height={'6'} />
											</Box>
											<Box paddingX={'2'}>
												<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Web.FIND_PENDING_SALES_ORDERS_UPDATE_CUSTOMER_BTN()}</Text>
											</Box>
										</VStack>																				
										<Separator orientation='vertical' height={'stretch'} size={'xs'} border={'1px solid'} borderColor={'neutral.300'} marginX={'1'} />
										<Menu.Root>
											<Menu.Trigger padding={'0'} height={'stretch'} _hover={{ background: 'none' }} border={'none'} alignItems={'flex-start'} asChild>
												<Button variant="outline" size="sm">
													<VStack height={'full'}>
														<Box padding={'2'}>
															<SvgIcon src={Images.JustPointMore} width={'6'} height={'6'} />
														</Box>
														<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'120%'}>{Res.Common.MORE_BTN()}</Text>
													</VStack>
												</Button>
											</Menu.Trigger>
											<Portal>
												<Menu.Positioner>
													<Menu.Content>
														<Menu.Item value={'convertToDeliveryNote'} {...onConvertToDeliveryNote} cursor={'pointer'}>{Res.Web.FIND_PENDING_SALES_ORDERS_CONVERT_TO_DELIVERY_NOTE_BTN()}</Menu.Item>
														<Menu.Item value={'prepare'} {...onPrepare} cursor={'pointer'}>{Res.Web.FIND_PENDING_SALES_ORDERS_PREPARE_BTN()}</Menu.Item>
														<Menu.Item value={'prepareAgain'} {...onPrepareAgain} cursor={'pointer'}>{Res.Web.FIND_PENDING_SALES_ORDERS_PREPARE_AGAIN_BTN()}</Menu.Item>
													</Menu.Content>
												</Menu.Positioner>
											</Portal>
										</Menu.Root>
									</HStack>								
									<VStack justifyContent={'flex-start'} alignItems={'flex-start'} overflowY={'auto'} overflowX={'hidden'}>
										<UI.TemplatePrintPreview preview={preview} />
									</VStack>
								</>
							)}
						</VStack>
					</HStack>
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<BackActionButton action={onCancel} />
		</Footer>
	</PosScreen>;
}

declare global {
	interface CommandType {
		showSalesOrders: () => Promise<Result<SaleModuleNextAction>>
	}
}

Command.register('showSalesOrders', Preconditions.and(Preconditions.ensure(Permission.SalesOrder_Complete), Preconditions.ensureIsNotLite), async (): Promise<Result<SaleModuleNextAction>> => {
	const initialSalesOrders = (await SalesOrderFacade.findByText('')).map(toModel);
	return Modal.show(ShowSalesOrdersScreen, { initialSalesOrders });
});
